import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyDentalChart, EmptyPerioChart } from '@constants/Charting'
import {
  IDentalChart,
  IDentalData,
  IPerioChart,
} from '@dataTypes/chartingFeature.types'
import { useActiveDentalChart } from '@hooks/useActiveDentalChart'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  addDentalChart,
  addPerioChart,
  updateDentalChart,
  updatePerioChart,
} from '@store/actions'
import {
  reconcilePerioChartSave,
  setCreateDentalChart,
  setCreatePerioChart,
  setDentalUpdated,
  setPerioUpdated,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setSelectedDentalChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import {
  buildDentalChartSignature,
  buildPerioChartSignature,
  ChartSaveSource,
  hasDentalChartableContent,
  hasNewDentalConditions,
  hasPerioChartableContent,
  getUniqueChartTitleResolution,
  mergeSavedDentalChart,
} from '@utils/chart-save.utils'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createDentalChartAddedToast,
  createDentalChartUpdatedToast,
  createFailedToSaveChartToast,
  createFailedToUpdateChartToast,
  createNewConditionChartedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import type { ChartingRouteTab } from './chartingNoteOverlay.util'

export type ChartPersistMode = 'manual' | 'autosave'

export interface ChartPersistOptions {
  mode: ChartPersistMode
  chartingTab: ChartingRouteTab
  keepEditingSession?: boolean
  silent?: boolean
}

export interface UseChartPersistResult {
  persistChart: (options: ChartPersistOptions) => Promise<boolean>
  exitEditingSession: (chartingTab: ChartingRouteTab) => void
  dentalSignature: string
  perioSignature: string
  canAutoSaveDental: boolean
  canAutoSavePerio: boolean
  resolveDentalChartForSave: () => IDentalChart
  resolvePerioChartForSave: () => IPerioChart
}

interface UseChartPersistParams {
  validateForManualSave: (chartingTab: ChartingRouteTab) => boolean
  validateForAutoSave: (chartingTab: ChartingRouteTab) => boolean
  syncTreatmentPlanCompletionAfterChartSave?: (
    savedDentalData: IDentalData[],
  ) => Promise<void>
}

export function useChartPersist({
  validateForManualSave,
  validateForAutoSave,
  syncTreatmentPlanCompletionAfterChartSave,
}: UseChartPersistParams): UseChartPersistResult {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const { activeDentalChart, isExplicitHistorySelection } =
    useActiveDentalChart()
  const {
    activePerioChart,
    isExplicitHistorySelection: isExplicitPerioHistorySelection,
  } = useActivePerioChart()

  const resolveDentalChartForSave = useCallback((): IDentalChart => {
    if (
      selectedDentalChart &&
      selectedDentalChart.chart_id !== createDentalChart.chart_id
    ) {
      return selectedDentalChart
    }
    const createChartId = createDentalChart.chart_id?.trim() ?? ''
    if (createChartId) {
      return createDentalChart
    }
    return activeDentalChart
  }, [activeDentalChart, createDentalChart, selectedDentalChart])

  const resolvePerioChartForSave = useCallback((): IPerioChart => {
    if (
      selectedPerioChart &&
      selectedPerioChart.chart_id !== createPerioChart.chart_id
    ) {
      return selectedPerioChart
    }
    const createChartId = createPerioChart.chart_id?.trim() ?? ''
    if (createChartId) {
      return createPerioChart
    }
    return activePerioChart
  }, [activePerioChart, createPerioChart, selectedPerioChart])

  const dentalSignature = useMemo(
    (): string => buildDentalChartSignature(resolveDentalChartForSave()),
    [resolveDentalChartForSave],
  )

  const perioSignature = useMemo(
    (): string => buildPerioChartSignature(resolvePerioChartForSave()),
    [resolvePerioChartForSave],
  )

  const canAutoSaveDental = useMemo((): boolean => {
    const chart = resolveDentalChartForSave()
    const patientId = chart.patient_id || selectedPatient?.patient_id || ''
    return (
      Boolean(patientId) &&
      hasDentalChartableContent(chart.dental_data) &&
      !isExplicitHistorySelection
    )
  }, [
    isExplicitHistorySelection,
    resolveDentalChartForSave,
    selectedPatient?.patient_id,
  ])

  const canAutoSavePerio = useMemo((): boolean => {
    const chart = resolvePerioChartForSave()
    const patientId = chart.patient_id || selectedPatient?.patient_id || ''
    return (
      Boolean(patientId) &&
      hasPerioChartableContent(chart.perio_data) &&
      !isExplicitPerioHistorySelection
    )
  }, [
    isExplicitPerioHistorySelection,
    resolvePerioChartForSave,
    selectedPatient?.patient_id,
  ])

  const validateForPersist = useCallback(
    (mode: ChartPersistMode, tab: ChartingRouteTab): boolean =>
      mode === 'manual' ? validateForManualSave(tab) : validateForAutoSave(tab),
    [validateForAutoSave, validateForManualSave],
  )

  const exitEditingSession = useCallback(
    (chartingTab: ChartingRouteTab): void => {
      const patientId = selectedPatient?.patient_id ?? ''
      const clinicId = selectedClinic?.clinic_id ?? ''
      if (chartingTab === 'perio') {
        const chartId = selectedPerioChart?.chart_id?.trim()
        const latestChart =
          chartId !== undefined && chartId !== ''
            ? (perioCharts.find((chart) => chart.chart_id === chartId) ??
              selectedPerioChart)
            : selectedPerioChart
        if (latestChart) {
          dispatch(setSelectedPerioChart(latestChart))
        }
        dispatch(
          setCreatePerioChart({
            ...EmptyPerioChart,
            patient_id: patientId,
            clinic_id: clinicId,
          }),
        )
        dispatch(setPreferCreatePerioChart(true))
        dispatch(setPerioUpdated(false))
        showToast(
          createSuccessToast(
            'Finished editing',
            'Chart is now in view-only mode.',
          ),
        )
        return
      }
      if (chartingTab === 'home') {
        const chartId = selectedDentalChart?.chart_id?.trim()
        const latestChart =
          chartId !== undefined && chartId !== ''
            ? (dentalCharts.find((chart) => chart.chart_id === chartId) ??
              selectedDentalChart)
            : selectedDentalChart
        if (latestChart) {
          dispatch(setSelectedDentalChart(latestChart))
        }
        dispatch(
          setCreateDentalChart({
            ...EmptyDentalChart,
            patient_id: patientId,
            clinic_id: clinicId,
          }),
        )
        dispatch(setPreferCreateDentalChart(true))
        dispatch(setDentalUpdated(false))
        showToast(
          createSuccessToast(
            'Finished editing',
            'Chart is now in view-only mode.',
          ),
        )
      }
    },
    [
      dentalCharts,
      dispatch,
      perioCharts,
      selectedClinic?.clinic_id,
      selectedDentalChart,
      selectedPatient?.patient_id,
      selectedPerioChart,
      showToast,
    ],
  )

  const persistChart = useCallback(
    async (options: ChartPersistOptions): Promise<boolean> => {
      const {
        mode,
        chartingTab,
        keepEditingSession = mode === 'autosave',
        silent = mode === 'autosave',
      } = options
      const saveSource: ChartSaveSource =
        mode === 'autosave' ? 'autosave' : 'manual'
      const viewChart = (): void => {
        navigate('/charting')
      }

      if (!validateForPersist(mode, chartingTab)) {
        return false
      }

      if (chartingTab === 'perio') {
        const perioChartToSave = resolvePerioChartForSave()
        const chartId = perioChartToSave.chart_id ?? ''
        const patientId =
          perioChartToSave.patient_id !== ''
            ? perioChartToSave.patient_id
            : (selectedPatient?.patient_id ?? '')
        const titleResolution = getUniqueChartTitleResolution(
          perioChartToSave.title ?? '',
          perioCharts,
          patientId,
          chartId,
        )
        const payload: IPerioChart = {
          chart_id: chartId,
          patient_id: patientId,
          provider_id: perioChartToSave.provider_id ?? '',
          clinic_id:
            perioChartToSave.clinic_id !== ''
              ? perioChartToSave.clinic_id
              : (selectedClinic?.clinic_id ?? ''),
          title: titleResolution.uniqueTitle,
          description: perioChartToSave.description ?? '',
          perio_data: perioChartToSave.perio_data,
        }
        try {
          const saved =
            chartId === ''
              ? await dispatch(
                  addPerioChart({ chart: payload, saveSource }),
                ).unwrap()
              : await dispatch(
                  updatePerioChart({
                    id: chartId,
                    chart: payload,
                    saveSource,
                  }),
                ).unwrap()
          dispatch(
            reconcilePerioChartSave({
              request: perioChartToSave,
              server: saved,
              target: isExplicitPerioHistorySelection ? 'selected' : 'create',
              keepEditingSession,
              resetPatientId: selectedPatient?.patient_id ?? '',
              resetClinicId: selectedClinic?.clinic_id ?? '',
            }),
          )
          if (!silent) {
            const patientName =
              `${selectedPatient?.first_name ?? ''} ${selectedPatient?.last_name ?? ''}`.trim()
            showToast(
              chartId === ''
                ? createDentalChartAddedToast(
                    titleResolution.uniqueTitle,
                    patientName,
                    viewChart,
                  )
                : createDentalChartUpdatedToast(
                    titleResolution.uniqueTitle,
                    patientName,
                    viewChart,
                  ),
            )
          }
          return true
        } catch (error) {
          if (!silent) {
            const message = error instanceof Error ? error.message : undefined
            showToast(
              chartId === ''
                ? createFailedToSaveChartToast(message)
                : createFailedToUpdateChartToast(message),
            )
          }
          return false
        }
      }

      if (chartingTab === 'home') {
        const dentalChartToSave = resolveDentalChartForSave()
        const chartId = dentalChartToSave.chart_id ?? ''
        const patientId =
          dentalChartToSave.patient_id !== ''
            ? dentalChartToSave.patient_id
            : (selectedPatient?.patient_id ?? '')
        const titleResolution = getUniqueChartTitleResolution(
          dentalChartToSave.title ?? '',
          dentalCharts,
          patientId,
          chartId,
        )
        const payload: IDentalChart = {
          chart_id: chartId,
          patient_id: patientId,
          provider_id: dentalChartToSave.provider_id ?? '',
          clinic_id:
            dentalChartToSave.clinic_id !== ''
              ? dentalChartToSave.clinic_id
              : (selectedClinic?.clinic_id ?? ''),
          title: titleResolution.uniqueTitle,
          description: dentalChartToSave.description ?? '',
          dental_data: dentalChartToSave.dental_data,
        }
        const previousDentalData =
          chartId === ''
            ? []
            : (dentalCharts.find((chart) => chart.chart_id === chartId)
                ?.dental_data ?? [])
        const addedNewCondition = hasNewDentalConditions(
          previousDentalData,
          dentalChartToSave.dental_data,
        )
        try {
          const saved =
            chartId === ''
              ? await dispatch(
                  addDentalChart({ chart: payload, saveSource }),
                ).unwrap()
              : await dispatch(
                  updateDentalChart({
                    id: chartId,
                    chart: payload,
                    saveSource,
                  }),
                ).unwrap()
          if (keepEditingSession) {
            const mergeBase = isExplicitHistorySelection
              ? (selectedDentalChart ?? dentalChartToSave)
              : createDentalChart.chart_id?.trim()
                ? createDentalChart
                : dentalChartToSave
            const merged = mergeSavedDentalChart(mergeBase, saved)
            if (isExplicitHistorySelection) {
              dispatch(setSelectedDentalChart(merged))
            } else {
              dispatch(setCreateDentalChart(merged))
              dispatch(setPreferCreateDentalChart(false))
            }
          } else {
            const mergeBase = isExplicitHistorySelection
              ? (selectedDentalChart ?? dentalChartToSave)
              : createDentalChart.chart_id?.trim()
                ? createDentalChart
                : dentalChartToSave
            const merged = mergeSavedDentalChart(mergeBase, saved)
            const persistedChartId = merged.chart_id?.trim()
            if (persistedChartId) {
              dispatch(setSelectedDentalChart(merged))
            }
            dispatch(
              setCreateDentalChart({
                ...EmptyDentalChart,
                patient_id: selectedPatient?.patient_id ?? '',
                clinic_id: selectedClinic?.clinic_id ?? '',
              }),
            )
            dispatch(setPreferCreateDentalChart(true))
          }
          dispatch(setDentalUpdated(false))
          if (syncTreatmentPlanCompletionAfterChartSave) {
            await syncTreatmentPlanCompletionAfterChartSave(
              saved.dental_data ?? [],
            )
          }
          if (addedNewCondition) {
            showToast(createNewConditionChartedToast(viewChart))
          } else if (!silent) {
            const patientName =
              `${selectedPatient?.first_name ?? ''} ${selectedPatient?.last_name ?? ''}`.trim()
            showToast(
              chartId === ''
                ? createDentalChartAddedToast(
                    titleResolution.uniqueTitle,
                    patientName,
                    viewChart,
                  )
                : createDentalChartUpdatedToast(
                    titleResolution.uniqueTitle,
                    patientName,
                    viewChart,
                  ),
            )
          }
          return true
        } catch (error) {
          if (!silent) {
            const message = error instanceof Error ? error.message : undefined
            showToast(
              chartId === ''
                ? createFailedToSaveChartToast(message)
                : createFailedToUpdateChartToast(message),
            )
          }
          return false
        }
      }

      return false
    },
    [
      createDentalChart,
      dentalCharts,
      dispatch,
      isExplicitHistorySelection,
      isExplicitPerioHistorySelection,
      navigate,
      perioCharts,
      resolveDentalChartForSave,
      resolvePerioChartForSave,
      selectedClinic?.clinic_id,
      selectedDentalChart,
      selectedPatient?.patient_id,
      selectedPatient?.first_name,
      selectedPatient?.last_name,
      showToast,
      syncTreatmentPlanCompletionAfterChartSave,
      validateForPersist,
    ],
  )

  return {
    persistChart,
    exitEditingSession,
    dentalSignature,
    perioSignature,
    canAutoSaveDental,
    canAutoSavePerio,
    resolveDentalChartForSave,
    resolvePerioChartForSave,
  }
}
